import React, { useCallback, useState } from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { LineChart } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { useFeatureEnabled } from '../../services/features';
import { useIsOrgAIEnabled } from '../../services/orgSettings';
import { useCurrentUser } from '../../services/currentUser';
import { SideDrawerReport } from '../SideDrawerReport';

interface LifecycleAnalysisResponse {
  hasAnalysis: boolean;
  templateId?: string;
  templateName?: string | null;
  analysis?: string;
  recordsAnalyzed?: number | null;
  etlRunId?: string | null;
  generatedAt?: string;
}

interface AILifecycleAnalysisProps {
  templateId: string;
}

const useStyles = makeStyles((theme) => ({
  button: {
    textTransform: 'none',
    fontSize: 13,
  },
  markdown: {
    fontSize: 14,
    lineHeight: 1.6,
    '& p': { margin: '0 0 10px 0', '&:last-child': { marginBottom: 0 } },
    '& ul, & ol': { margin: '6px 0', paddingLeft: 20 },
    '& li': { marginBottom: 3 },
    '& strong': { fontWeight: 600 },
    '& h1, & h2, & h3, & h4': { fontSize: 14, fontWeight: 600, margin: '14px 0 6px' },
    '& table': { borderCollapse: 'collapse', width: '100%', margin: '10px 0' },
    '& th, & td': { border: '1px solid rgba(0,0,0,0.12)', padding: '6px 10px', fontSize: 13, textAlign: 'left' },
    '& th': { fontWeight: 600, backgroundColor: 'rgba(0,0,0,0.04)' },
    '& code': { backgroundColor: 'rgba(0,0,0,0.06)', borderRadius: 3, padding: '1px 4px', fontSize: 13 },
    '& hr': { border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)', margin: '14px 0' },
  },
}));

function formatRelative(iso?: string): string | undefined {
  if (!iso) return undefined;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return undefined;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'Generated just now';
  if (diffSec < 3600) return `Generated ${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `Generated ${Math.floor(diffSec / 3600)}h ago`;
  return `Generated ${Math.floor(diffSec / 86400)}d ago`;
}

export const AILifecycleAnalysis: React.FC<AILifecycleAnalysisProps> = ({ templateId }) => {
  const classes = useStyles();
  const { org } = useCurrentUser();
  const hasFlag = useFeatureEnabled('AI_TEMPLATE_HEALTH');
  const isAIEnabled = useIsOrgAIEnabled(org.id);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LifecycleAnalysisResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const response = await callFirebaseFunction<LifecycleAnalysisResponse>(
        'ai_getLifecycleAnalysis',
        { organization_id: org.id, template_id: templateId }
      );
      setResult(response);
    } catch (err) {
      console.error('Failed to fetch lifecycle analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  }, [org.id, templateId]);

  if (!hasFlag || !isAIEnabled) return null;

  const hasData = !!result?.hasAnalysis && !!result.analysis;
  const isEmpty = !loading && !error && !hasData;

  return (
    <>
      <Button
        className={classes.button}
        size="small"
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        startIcon={<LineChart size={14} />}
      >
        AI Analysis
      </Button>

      <SideDrawerReport
        open={open}
        onClose={() => setOpen(false)}
        icon={<LineChart size={18} />}
        title="Lifecycle Analysis"
        subtitle={hasData ? formatRelative(result!.generatedAt) : undefined}
        loading={loading}
        loadingText="Loading analysis..."
        empty={isEmpty || !!error}
        emptyText={
          error
            ? `Couldn't load analysis: ${error}`
            : 'No analysis available yet. An analysis is generated automatically when lifecycle data is processed for this template.'
        }
      >
        {hasData && (
          <div className={classes.markdown}>
            <ReactMarkdown>{result!.analysis!}</ReactMarkdown>
          </div>
        )}
      </SideDrawerReport>
    </>
  );
};
